import { useCallback } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseHarmonicEditDialogHandlerProps {
  editingHarmonicGroup: any | null;
  verticalLines: VerticalLine[];
  replaceMarkersOnHarmonicCreation: boolean;
  removeVerticalLine: (lineId: string) => void;
  addVerticalLine: (
    timestamp: any,
    values: any,
    xValue: number,
    label: string,
    color: string,
    isHarmonic: boolean,
    isFirstHarmonic: boolean,
    chartId: string,
    sectionKey: string,
    markerType: string | undefined,
    originalPrefix: string | undefined,
    groupId: string
  ) => void;
}

export interface UseHarmonicEditDialogHandlerReturn {
  handleSave: (
    labelPrefix: string,
    multiplier: number,
    startingPoint: number,
    intervalType?: 'seconds' | 'minutes' | 'hours' | 'days',
    interval?: number
  ) => void;
}

/**
 * Handles HarmonicEditDialog save action
 * Creates or updates harmonic groups (H, I, J) with configurable multipliers and intervals
 */
export function useHarmonicEditDialogHandler({
  editingHarmonicGroup,
  verticalLines,
  replaceMarkersOnHarmonicCreation,
  removeVerticalLine,
  addVerticalLine,
}: UseHarmonicEditDialogHandlerProps): UseHarmonicEditDialogHandlerReturn {

  const handleSave = useCallback(
    (
      labelPrefix: string,
      multiplier: number,
      startingPoint: number,
      intervalType?: 'seconds' | 'minutes' | 'hours' | 'days',
      interval?: number
    ) => {
      if (editingHarmonicGroup) {
        // Find all lines in the old group (only if editing existing group)
        const oldGroupLines = verticalLines.filter(
          (line: VerticalLine) =>
            line.isHarmonic &&
            line.color === editingHarmonicGroup.color &&
            line.chartId === editingHarmonicGroup.chartId
        );

        // Determine original prefix to preserve
        let originalPrefix: string | undefined = undefined;

        if (!editingHarmonicGroup.isNewGroup && oldGroupLines.length > 0) {
          // If editing existing group, check if it has an originalPrefix or extract from label
          const firstLine = oldGroupLines[0];
          if (firstLine.originalPrefix) {
            // Already has originalPrefix (was renamed before)
            originalPrefix = firstLine.originalPrefix;
          } else {
            // Extract current prefix - this is the original default
            const match = firstLine.label?.match(/^-?([A-Z])\d+/);
            if (match) {
              const currentPrefix = match[1];
              // Only set originalPrefix if the new labelPrefix is different (= custom name)
              if (currentPrefix !== labelPrefix) {
                originalPrefix = currentPrefix;
              }
            }
          }
        }

        // Remove old group lines (only if editing, not creating new)
        if (!editingHarmonicGroup.isNewGroup) {
          oldGroupLines.forEach((line: VerticalLine) => {
            removeVerticalLine(line.id);
          });
        } else {
          // If creating new group and replaceMarkersOnHarmonicCreation is true, remove all markers for this section
          if (replaceMarkersOnHarmonicCreation && editingHarmonicGroup.sectionKey) {
            const sectionKey = editingHarmonicGroup.sectionKey;
            const markersToRemove = verticalLines.filter(
              (line: VerticalLine) => !line.isHarmonic && line.sectionKey === sectionKey
            );
            markersToRemove.forEach((line: VerticalLine) => {
              removeVerticalLine(line.id);
            });
          }
        }

        // Generate new group with updated settings
        const intervalMs = {
          seconds: 1000,
          minutes: 60 * 1000,
          hours: 60 * 60 * 1000,
          days: 24 * 60 * 60 * 1000,
        };

        // Use sectionKey from editingHarmonicGroup (always available)
        const sectionKey = editingHarmonicGroup.sectionKey;

        // Generate unique group ID for this harmonic group (all harmonics in this group share this ID)
        const groupId = editingHarmonicGroup.isNewGroup
          ? `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          : oldGroupLines.length > 0 && oldGroupLines[0].groupId
          ? oldGroupLines[0].groupId
          : `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Add starting point with new label
        // IMPORTANT: Harmonics start from 1 (H1 is fundamental frequency), NOT 0
        const startLabel = `${labelPrefix}1`;
        addVerticalLine(
          editingHarmonicGroup.xAxisType === 'date' ? new Date(startingPoint) : startingPoint,
          {},
          startingPoint,
          startLabel,
          editingHarmonicGroup.color,
          true, // isHarmonic
          true, // isFirstHarmonic
          editingHarmonicGroup.chartId,
          sectionKey,
          undefined, // markerType
          originalPrefix, // originalPrefix to track if renamed
          groupId // groupId - unique ID for this harmonic group
        );

        // Add harmonic lines with new multiplier
        // Start from 2 since we already created H1 above
        for (let i = 2; i <= multiplier; i++) {
          let harmonicValue: number;

          if (editingHarmonicGroup.xAxisType === 'date') {
            harmonicValue = startingPoint + (i - 1) * intervalMs[intervalType || 'hours'];
          } else {
            // Numeric: multiply for harmonics (2x, 3x, 4x, etc.)
            harmonicValue = startingPoint * i;
          }

          const label = `${labelPrefix}${i}`;
          addVerticalLine(
            editingHarmonicGroup.xAxisType === 'date' ? new Date(harmonicValue) : harmonicValue,
            {},
            harmonicValue,
            label,
            editingHarmonicGroup.color,
            true, // isHarmonic
            false, // isFirstHarmonic
            editingHarmonicGroup.chartId,
            sectionKey,
            undefined, // markerType
            originalPrefix, // originalPrefix to track if renamed
            groupId // groupId - unique ID for this harmonic group
          );
        }
      }
    },
    [editingHarmonicGroup, verticalLines, replaceMarkersOnHarmonicCreation, removeVerticalLine, addVerticalLine]
  );

  return {
    handleSave,
  };
}
